export type ProfileSettingSource = 'user' | 'org' | 'global' | 'none' | string | undefined;
export type ProfileSettingConflictDecision = 'cloudWins' | 'uploadLocal';

export interface RejectSelectionSnapshot {
  selectedKeys: string[];
  selectedLabels?: string[];
}

type LegacyRejectSelectionSnapshot = RejectSelectionSnapshot;

function lookupNestedSetting(
  settings: any,
  settingName?: string,
  grainId?: string,
  profileId?: string
) {
  if (!settings || typeof settings !== 'object') {
    return undefined;
  }

  const settingBucket = settingName ? settings?.[settingName] : settings;
  if (settingBucket === undefined || settingBucket === null) {
    return undefined;
  }

  if (Array.isArray(settingBucket)) {
    return settingBucket;
  }

  const grainBucket = grainId !== undefined && grainId !== null
    ? settingBucket?.[`${grainId}`] ?? settingBucket?.[grainId]
    : undefined;
  if (Array.isArray(grainBucket)) {
    return grainBucket;
  }

  const profileBucket = grainBucket && profileId !== undefined && profileId !== null
    ? grainBucket?.[`${profileId}`] ?? grainBucket?.[profileId]
    : undefined;
  if (profileBucket !== undefined) {
    return profileBucket;
  }

  if (profileId !== undefined && profileId !== null) {
    const directProfileBucket = settingBucket?.[`${profileId}`] ?? settingBucket?.[profileId];
    if (directProfileBucket !== undefined) {
      return directProfileBucket;
    }
  }

  return settingBucket;
}

export function stableProfileSettingJson(value: any): string {
  return JSON.stringify(value ?? null);
}

export function stableRejectSelectionJson(value: LegacyRejectSelectionSnapshot | undefined): string {
  return stableProfileSettingJson({
    selectedKeys: Array.isArray(value?.selectedKeys) ? [...value.selectedKeys].sort() : [],
  });
}

export function isRejectSelectionSettingName(settingName?: string): boolean {
  const normalized = `${settingName || ''}`.toLowerCase();
  return normalized.includes('reject_config')
    || normalized.includes('rejected_grains')
    || normalized.includes('particle_distribution_reject');
}

export function extractProfileSettingValue(params: {
  response: any;
  settingName?: string;
  grainId?: string;
  profileId?: string;
}) {
  const { response, settingName, grainId, profileId } = params;
  const settings = response?.settings;
  if (settings === undefined) {
    return response;
  }

  const nestedValue = lookupNestedSetting(settings, settingName, grainId, profileId);
  return nestedValue === undefined ? settings : nestedValue;
}

export function decideProfileSettingConflict(params: {
  remoteValue: any;
  remoteSource?: ProfileSettingSource;
  lastSyncedValue?: any;
  settingName?: string;
}): ProfileSettingConflictDecision {
  const { remoteValue, lastSyncedValue } = params;
  const remoteSource = params.remoteSource || (remoteValue === null || remoteValue === undefined ? 'none' : 'user');
  const hasExplicitCloudSetting = remoteSource === 'user' || remoteSource === 'org';
  const remoteHasValue = remoteValue !== null && remoteValue !== undefined;

  if (!hasExplicitCloudSetting || !remoteHasValue) {
    return 'uploadLocal';
  }

  if (lastSyncedValue === undefined) {
    return 'cloudWins';
  }

  if (isRejectSelectionSettingName(params.settingName)) {
    return stableRejectSelectionJson(remoteValue) !== stableRejectSelectionJson(lastSyncedValue)
      ? 'cloudWins'
      : 'uploadLocal';
  }

  return stableProfileSettingJson(remoteValue) !== stableProfileSettingJson(lastSyncedValue)
    ? 'cloudWins'
    : 'uploadLocal';
}

export function isProfileSettingAuthError(error: any): boolean {
  return error?.status === 401 || error?.statusCode === 401 || error?.type === 401;
}
